import { useEffect, useRef, useState } from 'react';
import { motion, useAnimationControls, useReducedMotion } from 'motion/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check } from 'lucide-react';
import { getRowEntrance } from '@/components/orders-drilldown/motion-table-row';
import { useImportErrorMessages } from '@/components/campaign-import/use-import-error-messages';
import { Input } from '@/components/ui/input';
import { useI18nContext } from '@/i18n/i18n-react';
import type {
  ImportRowError,
  ImportRowField,
  ImportRowValues,
} from '@/lib/campaign-import/row-validation';
import { cn } from '@/lib/utils';

export const GRID_ENTRANCE_ROWS = 12;
// Measured heights: a valid row is one input line, each error adds one line below it.
const VALID_ROW_HEIGHT = 53;
const ERROR_LINE_HEIGHT = 28;
const OVERSCAN = 6;

/** The grid column each validated field lives in. */
const FIELD_COLUMN: Record<ImportRowField, keyof ImportRowValues> = {
  name: 'name',
  launchDateTime: 'launchDate',
  notificationTitle: 'notificationTitle',
  notificationBody: 'notificationBody',
  notificationLink: 'notificationLink',
  utmCampaign: 'utmCampaign',
};

export interface ReviewGridRow {
  /** Index in the file, which is what `onEdit` reports. */
  index: number;
  rowNumber: number;
  /** What the cell shows and edits (the CSV value or the user's correction). */
  values: ImportRowValues;
  /** What the field becomes when left empty (store home link, utm from the name). */
  defaults: Pick<ImportRowValues, 'notificationLink' | 'utmCampaign'>;
  errors: ImportRowError[];
}

export interface ReviewGridProps {
  rows: ReviewGridRow[];
  hasTimeColumn: boolean;
  storeWebsiteDomain: string;
  onEdit: (
    rowIndex: number,
    field: keyof ImportRowValues,
    value: string
  ) => void;
}

interface ColumnDef {
  key: keyof ImportRowValues;
  /** Narrowest the column gets, in rem. Below the sum of these the grid scrolls sideways. */
  minRem: number;
  /** Share of the width left over when the grid is wider than the minimums. */
  grow: number;
}

const ROW_NUMBER_COLUMN_REM = 3;

const columnTrack = ({ minRem, grow }: Pick<ColumnDef, 'minRem' | 'grow'>) =>
  `minmax(${minRem}rem, ${grow}fr)`;

function useColumnLabel() {
  const { LL } = useI18nContext();
  return (key: keyof ImportRowValues): string => {
    switch (key) {
      case 'name':
        return LL.campaignImport.review.columnHeaders.name();
      case 'launchDate':
        return LL.campaignImport.review.columnHeaders.launchDate();
      case 'launchTime':
        return LL.campaignImport.review.columnHeaders.launchTime();
      case 'notificationTitle':
        return LL.campaignImport.review.columnHeaders.notificationTitle();
      case 'notificationBody':
        return LL.campaignImport.review.columnHeaders.notificationBody();
      case 'notificationLink':
        return LL.campaignImport.review.columnHeaders.notificationLink();
      case 'utmCampaign':
        return LL.campaignImport.review.columnHeaders.utmCampaign();
      case 'labels':
        return LL.campaignImport.mapping.fields.labels();
    }
  };
}

function GridRow({
  row,
  columns,
  template,
  position,
  entrance,
  onEdit,
  storeWebsiteDomain,
}: {
  row: ReviewGridRow;
  columns: ColumnDef[];
  template: string;
  position: number;
  entrance: boolean;
  onEdit: ReviewGridProps['onEdit'];
  storeWebsiteDomain: string;
}) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion();
  const messages = useImportErrorMessages(storeWebsiteDomain);
  const flash = useAnimationControls();
  const wasValid = useRef(row.errors.length === 0);
  const isValid = row.errors.length === 0;

  useEffect(() => {
    if (isValid && !wasValid.current && !reduceMotion) {
      void flash.start({
        backgroundColor: [
          'rgba(248,113,113,0.28)',
          'rgba(52,211,153,0.28)',
          'rgba(52,211,153,0)',
        ],
        transition: { duration: 1.1, times: [0, 0.35, 1] },
      });
    }
    wasValid.current = isValid;
  }, [isValid, reduceMotion, flash]);

  const invalidColumns = new Set(
    row.errors.map((error) => FIELD_COLUMN[error.field])
  );
  const labelOf = useColumnLabel();

  return (
    <motion.div
      role="row"
      aria-rowindex={position + 2}
      data-row-number={row.rowNumber}
      data-valid={isValid}
      animate={flash}
      className="border-b border-white/10"
    >
      <motion.div
        role="presentation"
        {...(entrance ? getRowEntrance(position) : {})}
        className="grid items-start gap-2 px-3 py-2"
        style={{ gridTemplateColumns: template }}
      >
        <div
          role="gridcell"
          className="flex items-center gap-1.5 pt-2 text-sm tabular-nums text-white/70"
        >
          {row.rowNumber}
          {isValid ? (
            <>
              <Check className="size-3.5 text-emerald-300" aria-hidden />
              <span className="sr-only">
                {LL.campaignImport.review.rowIsValid()}
              </span>
            </>
          ) : null}
        </div>
        {columns.map(({ key }) => {
          const invalid = invalidColumns.has(key);
          const placeholder =
            key === 'notificationLink' || key === 'utmCampaign'
              ? row.defaults[key]
              : undefined;
          return (
            <div role="gridcell" key={key}>
              <Input
                value={row.values[key]}
                placeholder={placeholder}
                aria-invalid={invalid}
                aria-label={LL.campaignImport.review.cellAriaLabel({
                  row: row.rowNumber,
                  field: labelOf(key),
                })}
                onChange={(event) => onEdit(row.index, key, event.target.value)}
                className={cn(
                  'h-9 border-white/15 bg-white/5 text-sm text-white placeholder:text-white/40',
                  invalid &&
                    'border-red-400 bg-red-400/10 focus-visible:ring-red-400'
                )}
              />
            </div>
          );
        })}
        {isValid ? null : (
          <div role="gridcell" className="col-span-full" data-slot="row-errors">
            <ul className="sticky left-3 w-fit space-y-0.5 text-sm text-red-200">
              {row.errors.map((error) => (
                <li key={`${error.field}-${error.code}`}>
                  {messages.describe(error)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

export function ReviewGrid({
  rows,
  hasTimeColumn,
  storeWebsiteDomain,
  onEdit,
}: ReviewGridProps) {
  const { LL } = useI18nContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [entrance, setEntrance] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => setEntrance(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  const columns: ColumnDef[] = [
    { key: 'name', minRem: 8, grow: 3 },
    { key: 'launchDate', minRem: 7, grow: 2 },
    ...(hasTimeColumn
      ? [{ key: 'launchTime', minRem: 4.5, grow: 1 } satisfies ColumnDef]
      : []),
    { key: 'notificationTitle', minRem: 8.5, grow: 3 },
    { key: 'notificationBody', minRem: 9, grow: 4 },
    { key: 'notificationLink', minRem: 8.5, grow: 3 },
    { key: 'utmCampaign', minRem: 6, grow: 2 },
  ];
  // The columns share the whole width; the last one is not left with the slack.
  const template = [
    `${ROW_NUMBER_COLUMN_REM}rem`,
    ...columns.map(columnTrack),
  ].join(' ');
  // Tracks + the gap between them (0.5rem) + the row padding (0.75rem a side).
  const minWidthRem =
    ROW_NUMBER_COLUMN_REM +
    columns.reduce((sum, c) => sum + c.minRem, 0) +
    columns.length * 0.5 +
    1.5;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const errorCount = rows[index].errors.length;
      return VALID_ROW_HEIGHT + errorCount * ERROR_LINE_HEIGHT;
    },
    // Measurements follow the row, not its position, so filtering or fixing rows does not
    // hand a row the height of whichever row used to sit at that index.
    getItemKey: (index) => rows[index].index,
    overscan: OVERSCAN,
  });

  const columnLabel = useColumnLabel();
  const headerLabels: string[] = [
    LL.campaignImport.review.columnHeaders.rowNumber(),
    ...columns.map(({ key }) => columnLabel(key)),
  ];

  return (
    <div
      ref={scrollRef}
      role="grid"
      aria-label={LL.campaignImport.review.gridLabel()}
      aria-rowcount={rows.length + 1}
      data-slot="review-grid-scroll"
      // The one scroll area: it hugs few rows and caps at the viewport for many, and
      // never hands its scroll over to the page (overscroll) or fights the virtualizer (anchor).
      className="max-h-[clamp(18rem,calc(100dvh-16rem),44rem)] overflow-auto overscroll-contain rounded-lg border border-white/10 bg-black/20 [overflow-anchor:none]"
    >
      <div style={{ minWidth: `${minWidthRem}rem` }}>
        <div
          role="row"
          aria-rowindex={1}
          className="sticky top-0 z-10 grid gap-2 border-b border-white/15 bg-[#15191E] px-3 py-2 text-xs font-semibold text-white/65"
          style={{ gridTemplateColumns: template }}
        >
          {headerLabels.map((label, index) => (
            <div role="columnheader" key={index}>
              {label}
            </div>
          ))}
        </div>
        <div
          className="relative"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <div
                key={row.index}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="absolute left-0 top-0 w-full"
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                <GridRow
                  row={row}
                  columns={columns}
                  template={template}
                  position={virtualRow.index}
                  entrance={entrance && virtualRow.index < GRID_ENTRANCE_ROWS}
                  onEdit={onEdit}
                  storeWebsiteDomain={storeWebsiteDomain}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
